import { api } from "@/shared/api";
import type {
  CampaignActionLogEntry,
  CampaignTechnicalLogEntry,
  CampaignDailyMetric,
  CampaignDetails,
  PaginatedCampaignsResponse,
  PaginatedActionLogsResponse,
  PaginatedTechnicalLogsResponse,
  ProblematicCampaignsResponse,
  CampaignActionDetailResponse,
  OptimizationActionItem,
} from "../model/types";

export interface FetchCampaignsParams {
  clientLogin?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export async function fetchCampaigns({
  clientLogin,
  search = "",
  page = 1,
  limit = 20,
}: FetchCampaignsParams = {}): Promise<PaginatedCampaignsResponse> {
  const res = await api.get<PaginatedCampaignsResponse>("/api/campaigns", {
    timeout: 60000,
    params: {
      client_login: clientLogin || undefined,
      search: search || undefined,
      page,
      limit,
    },
  });
  return res.data;
}

export async function fetchCampaignDetails(
  id: string | number
): Promise<CampaignDetails> {
  const res = await api.get<CampaignDetails>(`/api/campaigns/${id}`, {
    timeout: 30000,
  });
  return res.data;
}

export async function fetchCampaignMetrics(
  id: string | number,
  days = 7
): Promise<CampaignDailyMetric[]> {
  const res = await api.get<CampaignDailyMetric[]>(`/api/campaigns/${id}/metrics`, {
    timeout: 30000,
    params: { days },
  });
  return res.data || [];
}

export async function fetchProblematicCampaigns(
  clientLogin?: string
): Promise<ProblematicCampaignsResponse> {
  const res = await api.get<ProblematicCampaignsResponse>("/api/campaigns/problems", {
    timeout: 60000,
    params: {
      client_login: clientLogin || undefined,
    },
  });
  return res.data || { total: 0, data: [] };
}

export async function toggleCampaignRule(
  campaignId: string | number,
  ruleId: number | string,
  targetStatus: "ENABLED" | "DISABLED"
): Promise<void> {
  await api.post(`/api/campaigns/${campaignId}/rules/${ruleId}/toggle`, {
    target_status: targetStatus,
  });
}



export interface FetchActionsParams {
  page?: number;
  limit?: number;
}

export async function fetchCampaignActions(
  campaignId: string | number,
  params: FetchActionsParams = {}
): Promise<PaginatedActionLogsResponse> {
  const res = await api.get<PaginatedActionLogsResponse>(
    `/api/campaigns/${campaignId}/actions`,
    {
      params: {
        page: params.page || 1,
        limit: params.limit || 20,
      },
    }
  );
  return res.data;
}

export async function fetchCampaignTechnicalLogs(
  campaignId: string | number,
  params: { page?: number; limit?: number; run_id?: string } = {}
): Promise<PaginatedTechnicalLogsResponse> {
  const res = await api.get<PaginatedTechnicalLogsResponse>(
    `/api/campaigns/${campaignId}/technical-logs`,
    {
      params: {
        page: params.page || 1,
        limit: params.limit || 50,
        run_id: params.run_id || undefined,
      },
    }
  );
  return res.data;
}

export async function fetchCampaignActionDetails(
  campaignId: string | number,
  actionId: string
): Promise<CampaignActionDetailResponse> {
  const res = await api.get<CampaignActionDetailResponse>(
    `/api/campaigns/${campaignId}/actions/${actionId}/details`,
    {
      timeout: 30000,
    }
  );
  return res.data;
}

export const getCampaignActionDetails = fetchCampaignActionDetails;
